import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { TableIcon } from '@dynatrace/strato-icons';

import { Tooltip } from '../../../../../overlays/tooltip/Tooltip.js';
import {
  useDataTableV2,
  useDataTableV2ColumnSettings,
} from '../../../contexts/DataTableV2Context.js';

type ColumnSettingsProps = {
  customTrigger?: (props: {
    hiddenColumnCount: number;
    totalColumnCount: number;
    defaultLabel: string;
  }) => string;
};

/**
 * DataTableV2 column settings. Internally used component containing a trigger button
 * that opens the column settings modal which displays a list of all columns.
 *
 * @internal
 */
export const DataTableV2ColumnSettings = (props: ColumnSettingsProps) => {
  const { customTrigger } = props;

  const intl = useIntl();
  const table = useDataTableV2();
  const columnSettingsModal = useDataTableV2ColumnSettings();

  const columnSettingsButton = useMemo(() => {
    const hiddenColumnCount = Object.values(
      table.getState().columnVisibility || {},
    ).filter((value) => !value).length;

    const message =
      hiddenColumnCount === 0
        ? intl.formatMessage({
            defaultMessage: 'Columns',
            id: 'F78crM1uQumIOJdh',
            description: 'Column settings button default text',
          })
        : intl.formatMessage(
            {
              defaultMessage:
                '{hiddenColumnCount} {hiddenColumnCount, plural, one {column} other {columns}} hidden',
              id: 'aqGvcDl8n+XdhJiq',
              description: 'Column visibility button text',
            },
            { hiddenColumnCount },
          );

    return {
      text: message,
      hiddenColumnCount,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState().columnVisibility]);

  return (
    <Tooltip
      text={intl.formatMessage({
        defaultMessage: 'Column settings',
        description: 'Tooltip information for the column settings button',
        id: 'DoSCFZmkm4Wgk+9J',
      })}
    >
      <Button
        onClick={columnSettingsModal?.openColumnSettings}
        variant="default"
      >
        <Button.Prefix>
          <TableIcon />
        </Button.Prefix>
        {customTrigger
          ? customTrigger({
              hiddenColumnCount: columnSettingsButton.hiddenColumnCount,
              totalColumnCount: table.getAllColumns().length,
              defaultLabel: columnSettingsButton.text,
            })
          : columnSettingsButton.text}
      </Button>
    </Tooltip>
  );
};
